import { useState } from 'react';

import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { DateField } from '@/components/forms/DateField';
import { BoundaryDrawingModal } from '@/components/map/BoundaryDrawingModal';
import type {
  FoodSourceMetadata,
  FoodSourceType,
  FoodPlotSubtype,
  FruitTreeSubtype,
  MineralSiteSubtype,
  FeederSubtype,
  NutsSubtype,
  BerriesSubtype,
  OtherFoodSourceSubtype,
  GeoJSONPolygon,
} from '@/types/waypoint';
import { computeAcreage } from '@/utils/boundary-utils';

interface FoodSourceFormProps {
  metadata: Partial<FoodSourceMetadata>;
  onChange: (updated: Partial<FoodSourceMetadata>) => void;
  /** Center point for the boundary mini map. If omitted, boundary drawing is hidden. */
  centerCoords?: [number, number];
}

const SUBTYPE_OPTIONS: { value: FoodSourceType; label: string }[] = [
  { value: 'food_plot', label: 'Food Plot' },
  { value: 'fruit_tree', label: 'Fruit Tree' },
  { value: 'mineral_site', label: 'Mineral Site' },
  { value: 'feeder', label: 'Feeder' },
  { value: 'nuts', label: 'Nuts / Mast' },
  { value: 'berries', label: 'Berries' },
  { value: 'other', label: 'Other' },
];

const TARGET_SEASON_OPTIONS = [
  { value: 'early_bow', label: 'Early Bow' },
  { value: 'peak_rut', label: 'Peak Rut' },
  { value: 'late_season', label: 'Late Season' },
  { value: 'winter', label: 'Winter' },
  { value: 'year_round', label: 'Year-Round' },
];

const TREE_SPECIES_OPTIONS = [
  { value: 'apple', label: 'Apple' },
  { value: 'pear', label: 'Pear' },
  { value: 'crabapple', label: 'Crabapple' },
  { value: 'persimmon', label: 'Persimmon' },
  { value: 'plum', label: 'Plum' },
  { value: 'cherry', label: 'Cherry' },
  { value: 'other', label: 'Other' },
];

const MATURITY_OPTIONS = [
  { value: 'seedling', label: 'Seedling' },
  { value: 'young', label: 'Young' },
  { value: 'producing', label: 'Producing' },
  { value: 'mature', label: 'Mature' },
];

const DROP_SEASON_OPTIONS = [
  { value: 'early_fall', label: 'Early Fall' },
  { value: 'mid_fall', label: 'Mid Fall' },
  { value: 'late_fall', label: 'Late Fall' },
  { value: 'winter', label: 'Winter' },
];

const FEEDER_TYPE_OPTIONS = [
  { value: 'gravity', label: 'Gravity' },
  { value: 'spin', label: 'Spin' },
  { value: 'trough', label: 'Trough' },
  { value: 'broadcast', label: 'Broadcast' },
  { value: 'other', label: 'Other' },
];

const NUT_SPECIES_OPTIONS = [
  { value: 'oak_white', label: 'White Oak' },
  { value: 'oak_red', label: 'Red Oak' },
  { value: 'hickory', label: 'Hickory' },
  { value: 'chestnut', label: 'Chestnut' },
  { value: 'beech', label: 'Beech' },
  { value: 'other', label: 'Other' },
];

const MAST_PRODUCTION_OPTIONS = [
  { value: 'poor', label: 'Poor' },
  { value: 'fair', label: 'Fair' },
  { value: 'good', label: 'Good' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'unknown', label: 'Unknown' },
];

const NUT_DROP_SEASON_OPTIONS = [
  { value: 'early_fall', label: 'Early Fall' },
  { value: 'mid_fall', label: 'Mid Fall' },
  { value: 'late_fall', label: 'Late Fall' },
];

const BERRY_SPECIES_OPTIONS = [
  { value: 'blackberry', label: 'Blackberry' },
  { value: 'raspberry', label: 'Raspberry' },
  { value: 'honeysuckle', label: 'Honeysuckle' },
  { value: 'sumac', label: 'Sumac' },
  { value: 'dogwood', label: 'Dogwood' },
  { value: 'pokeberry', label: 'Pokeberry' },
  { value: 'greenbrier', label: 'Greenbrier' },
  { value: 'other', label: 'Other' },
];

const AREA_SIZE_OPTIONS = [
  { value: 'patch', label: 'Patch' },
  { value: 'thicket', label: 'Thicket' },
  { value: 'scattered', label: 'Scattered' },
];

const BERRY_PEAK_SEASON_OPTIONS = [
  { value: 'summer', label: 'Summer' },
  { value: 'early_fall', label: 'Early Fall' },
  { value: 'mid_fall', label: 'Mid Fall' },
  { value: 'late_fall', label: 'Late Fall' },
];

const PLANTED_OR_NATURAL_OPTIONS = [
  { value: 'planted', label: 'Planted' },
  { value: 'natural', label: 'Natural' },
];

const SEASON_AVAILABLE_OPTIONS = [
  { value: 'spring', label: 'Spring' },
  { value: 'summer', label: 'Summer' },
  { value: 'fall', label: 'Fall' },
  { value: 'winter', label: 'Winter' },
  { value: 'year_round', label: 'Year Round' },
];

// Allow free text for plant names; reject HTML/script injection vectors.
function isValidPlantedSpecies(value: string): boolean {
  const stripped = value.replace(/<[^>]*>/g, '');
  return stripped === value && value.length <= 500;
}

/** Convert lat/lng vertex array to GeoJSONPolygon (closed ring). */
function verticesToGeoJSON(vertices: [number, number][]): GeoJSONPolygon {
  const coords = vertices.map(([lat, lng]) => [lng, lat] as [number, number]);
  return {
    type: 'Polygon',
    coordinates: [[...coords, coords[0]]],
  };
}

/** Extract lat/lng vertex array from a GeoJSONPolygon (drops closing duplicate). */
function geoJSONToVertices(polygon: GeoJSONPolygon): [number, number][] {
  const ring = polygon.coordinates[0];
  const open =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring;
  return open.map(([lng, lat]) => [lat, lng]);
}

function FoodPlotFields({
  metadata,
  onChange,
}: {
  metadata: Partial<FoodPlotSubtype>;
  onChange: (updated: Partial<FoodPlotSubtype>) => void;
}) {
  const [speciesError, setSpeciesError] = useState<string | undefined>(undefined);
  const speciesString = (metadata.plantedSpecies ?? []).join(', ');

  function handleSpeciesChange(raw: string) {
    if (!isValidPlantedSpecies(raw)) {
      setSpeciesError('HTML tags are not allowed');
      return;
    }
    setSpeciesError(undefined);
    const parsed = raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    onChange({ ...metadata, plantedSpecies: parsed });
  }

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Input
          id="food-source-species"
          label="Planted Species"
          value={speciesString}
          onChange={handleSpeciesChange}
          placeholder="e.g. Clover, Brassica, Oats"
          error={speciesError}
        />
        <p className="text-body-xs text-[var(--color-text-placeholder)]">Separate multiple species with commas</p>
      </div>

      <Select
        id="food-source-target-season"
        label="Target Season Draw"
        value={metadata.targetSeasonDraw ?? ''}
        onChange={(v) =>
          onChange({ ...metadata, targetSeasonDraw: v as FoodPlotSubtype['targetSeasonDraw'] })
        }
        options={TARGET_SEASON_OPTIONS}
      />

      <DateField
        id="food-source-soil-test-date"
        label="Soil Test Date"
        value={metadata.soilTestDate ?? ''}
        onChange={(v) => onChange({ ...metadata, soilTestDate: v || undefined })}
      />

      <Input
        id="food-source-soil-ph"
        label="Soil pH"
        type="number"
        value={metadata.soilPH !== undefined ? String(metadata.soilPH) : ''}
        onChange={(v) => onChange({ ...metadata, soilPH: v === '' ? undefined : Number(v) })}
        placeholder="6.5"
      />

      <DateField
        id="food-source-last-planted"
        label="Last Planted Date"
        value={metadata.lastPlantedDate ?? ''}
        onChange={(v) => onChange({ ...metadata, lastPlantedDate: v || undefined })}
      />

      <DateField
        id="food-source-last-fertilized"
        label="Last Fertilized Date"
        value={metadata.lastFertilizedDate ?? ''}
        onChange={(v) => onChange({ ...metadata, lastFertilizedDate: v || undefined })}
      />
    </>
  );
}

function FruitTreeFields({
  metadata,
  onChange,
}: {
  metadata: Partial<FruitTreeSubtype>;
  onChange: (updated: Partial<FruitTreeSubtype>) => void;
}) {
  return (
    <>
      <Select
        id="food-source-tree-species"
        label="Tree Species"
        value={metadata.treeSpecies ?? ''}
        onChange={(v) =>
          onChange({ ...metadata, treeSpecies: v as FruitTreeSubtype['treeSpecies'] })
        }
        options={TREE_SPECIES_OPTIONS}
      />

      <Input
        id="food-source-num-trees"
        label="Number of Trees"
        type="number"
        value={metadata.numberOfTrees !== undefined ? String(metadata.numberOfTrees) : ''}
        onChange={(v) =>
          onChange({ ...metadata, numberOfTrees: v === '' ? undefined : Number(v) })
        }
        placeholder="3"
      />

      <DateField
        id="food-source-planted-date"
        label="Planted Date"
        value={metadata.plantedDate ?? ''}
        onChange={(v) => onChange({ ...metadata, plantedDate: v || undefined })}
      />

      <Select
        id="food-source-maturity"
        label="Maturity"
        value={metadata.maturity ?? ''}
        onChange={(v) => onChange({ ...metadata, maturity: v as FruitTreeSubtype['maturity'] })}
        options={MATURITY_OPTIONS}
      />

      <Select
        id="food-source-drop-season"
        label="Drop Season"
        value={metadata.dropSeason ?? ''}
        onChange={(v) =>
          onChange({ ...metadata, dropSeason: v as FruitTreeSubtype['dropSeason'] })
        }
        options={DROP_SEASON_OPTIONS}
      />

      <div className="flex items-center gap-3">
        <label className="field-label">
          Fenced / Protected
        </label>
        <input
          id="food-source-fenced"
          type="checkbox"
          checked={metadata.fenced ?? false}
          onChange={(e) => onChange({ ...metadata, fenced: e.target.checked })}
          className="w-4 h-4 rounded accent-ember-500"
        />
      </div>
    </>
  );
}

function MineralSiteFields({
  metadata,
  onChange,
}: {
  metadata: Partial<MineralSiteSubtype>;
  onChange: (updated: Partial<MineralSiteSubtype>) => void;
}) {
  return (
    <>
      <Input
        id="food-source-mineral-type"
        label="Mineral Type"
        value={metadata.mineralType ?? ''}
        onChange={(v) => onChange({ ...metadata, mineralType: v || undefined })}
        placeholder="e.g. salt block, Trophy Rock, loose mineral"
      />

      <DateField
        id="food-source-last-refreshed"
        label="Last Refreshed Date"
        value={metadata.lastRefreshedDate ?? ''}
        onChange={(v) => onChange({ ...metadata, lastRefreshedDate: v || undefined })}
      />

      <Input
        id="food-source-refresh-interval"
        label="Refresh Interval (days)"
        type="number"
        value={
          metadata.refreshIntervalDays !== undefined ? String(metadata.refreshIntervalDays) : ''
        }
        onChange={(v) =>
          onChange({ ...metadata, refreshIntervalDays: v === '' ? undefined : Number(v) })
        }
        placeholder="30"
      />
    </>
  );
}

function FeederFields({
  metadata,
  onChange,
}: {
  metadata: Partial<FeederSubtype>;
  onChange: (updated: Partial<FeederSubtype>) => void;
}) {
  return (
    <>
      <Select
        id="food-source-feeder-type"
        label="Feeder Type"
        value={metadata.feederType ?? ''}
        onChange={(v) => onChange({ ...metadata, feederType: v as FeederSubtype['feederType'] })}
        options={FEEDER_TYPE_OPTIONS}
      />

      <Input
        id="food-source-capacity"
        label="Capacity (lbs)"
        type="number"
        value={metadata.capacityLbs !== undefined ? String(metadata.capacityLbs) : ''}
        onChange={(v) =>
          onChange({ ...metadata, capacityLbs: v === '' ? undefined : Number(v) })
        }
        placeholder="200"
      />

      <Input
        id="food-source-feed-type"
        label="Feed Type"
        value={metadata.feedType ?? ''}
        onChange={(v) => onChange({ ...metadata, feedType: v || undefined })}
        placeholder="e.g. corn, protein pellets"
      />

      <DateField
        id="food-source-last-filled"
        label="Last Filled Date"
        value={metadata.lastFilledDate ?? ''}
        onChange={(v) => onChange({ ...metadata, lastFilledDate: v || undefined })}
      />

      <Input
        id="food-source-fill-interval"
        label="Fill Interval (days)"
        type="number"
        value={metadata.fillIntervalDays !== undefined ? String(metadata.fillIntervalDays) : ''}
        onChange={(v) =>
          onChange({ ...metadata, fillIntervalDays: v === '' ? undefined : Number(v) })
        }
        placeholder="14"
      />

      <Input
        id="food-source-timer-schedule"
        label="Timer / Schedule"
        value={metadata.timerSchedule ?? ''}
        onChange={(v) => onChange({ ...metadata, timerSchedule: v || undefined })}
        placeholder="e.g. 6am and 5pm, 5 seconds"
      />

      <Input
        id="food-source-battery-type"
        label="Battery Type"
        value={metadata.batteryType ?? ''}
        onChange={(v) => onChange({ ...metadata, batteryType: v || undefined })}
        placeholder="e.g. 6V, solar"
      />

      <DateField
        id="food-source-last-battery"
        label="Last Battery Change"
        value={metadata.lastBatteryChange ?? ''}
        onChange={(v) => onChange({ ...metadata, lastBatteryChange: v || undefined })}
      />
    </>
  );
}

function NutsFields({
  metadata,
  onChange,
}: {
  metadata: Partial<NutsSubtype>;
  onChange: (updated: Partial<NutsSubtype>) => void;
}) {
  return (
    <>
      <Select
        id="food-source-nut-species"
        label="Nut Species"
        value={metadata.nutSpecies ?? ''}
        onChange={(v) => onChange({ ...metadata, nutSpecies: v as NutsSubtype['nutSpecies'] })}
        options={NUT_SPECIES_OPTIONS}
      />

      <Input
        id="food-source-nut-trees"
        label="Number of Trees"
        type="number"
        value={metadata.numberOfTrees !== undefined ? String(metadata.numberOfTrees) : ''}
        onChange={(v) =>
          onChange({ ...metadata, numberOfTrees: v === '' ? undefined : Number(v) })
        }
        placeholder="5"
      />

      <Select
        id="food-source-mast-production"
        label="Mast Production"
        value={metadata.mastProduction ?? ''}
        onChange={(v) =>
          onChange({ ...metadata, mastProduction: v as NutsSubtype['mastProduction'] })
        }
        options={MAST_PRODUCTION_OPTIONS}
      />

      <Select
        id="food-source-nut-drop-season"
        label="Drop Season"
        value={metadata.dropSeason ?? ''}
        onChange={(v) =>
          onChange({ ...metadata, dropSeason: v as NutsSubtype['dropSeason'] })
        }
        options={NUT_DROP_SEASON_OPTIONS}
      />

      <Input
        id="food-source-nut-notes"
        label="Notes"
        value={metadata.notes ?? ''}
        onChange={(v) => onChange({ ...metadata, notes: v || undefined })}
        placeholder="Additional notes"
      />
    </>
  );
}

function BerriesFields({
  metadata,
  onChange,
}: {
  metadata: Partial<BerriesSubtype>;
  onChange: (updated: Partial<BerriesSubtype>) => void;
}) {
  return (
    <>
      <Select
        id="food-source-berry-species"
        label="Berry Species"
        value={metadata.berrySpecies ?? ''}
        onChange={(v) =>
          onChange({ ...metadata, berrySpecies: v as BerriesSubtype['berrySpecies'] })
        }
        options={BERRY_SPECIES_OPTIONS}
      />

      <Select
        id="food-source-area-size"
        label="Area Size"
        value={metadata.areaSize ?? ''}
        onChange={(v) => onChange({ ...metadata, areaSize: v as BerriesSubtype['areaSize'] })}
        options={AREA_SIZE_OPTIONS}
      />

      <Select
        id="food-source-berry-peak-season"
        label="Peak Season"
        value={metadata.peakSeason ?? ''}
        onChange={(v) =>
          onChange({ ...metadata, peakSeason: v as BerriesSubtype['peakSeason'] })
        }
        options={BERRY_PEAK_SEASON_OPTIONS}
      />

      <Select
        id="food-source-planted-or-natural"
        label="Planted or Natural"
        value={metadata.plantedOrNatural ?? ''}
        onChange={(v) =>
          onChange({ ...metadata, plantedOrNatural: v as BerriesSubtype['plantedOrNatural'] })
        }
        options={PLANTED_OR_NATURAL_OPTIONS}
      />

      <Input
        id="food-source-berry-notes"
        label="Notes"
        value={metadata.notes ?? ''}
        onChange={(v) => onChange({ ...metadata, notes: v || undefined })}
        placeholder="Additional notes"
      />
    </>
  );
}

function OtherFoodSourceFields({
  metadata,
  onChange,
}: {
  metadata: Partial<OtherFoodSourceSubtype>;
  onChange: (updated: Partial<OtherFoodSourceSubtype>) => void;
}) {
  return (
    <>
      <Input
        id="food-source-description"
        label="Description"
        value={metadata.description ?? ''}
        onChange={(v) => onChange({ ...metadata, description: v || undefined })}
        placeholder="Describe this food source"
      />

      <Select
        id="food-source-season-available"
        label="Season Available"
        value={metadata.seasonAvailable ?? ''}
        onChange={(v) =>
          onChange({
            ...metadata,
            seasonAvailable: v as OtherFoodSourceSubtype['seasonAvailable'],
          })
        }
        options={SEASON_AVAILABLE_OPTIONS}
      />

      <Input
        id="food-source-other-notes"
        label="Notes"
        value={metadata.notes ?? ''}
        onChange={(v) => onChange({ ...metadata, notes: v || undefined })}
        placeholder="Additional notes"
      />
    </>
  );
}

export function FoodSourceForm({ metadata, onChange, centerCoords }: FoodSourceFormProps) {
  const foodSourceType: FoodSourceType = metadata.foodSourceType ?? 'food_plot';
  const [boundaryModalOpen, setBoundaryModalOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const hasBoundary = !!metadata.boundaryGeoJSON;
  const calculatedAcreage = metadata.plotSizeAcresCalculated;

  const currentVertices = metadata.boundaryGeoJSON
    ? geoJSONToVertices(metadata.boundaryGeoJSON)
    : undefined;

  function handleSubtypeChange(newSubtype: FoodSourceType) {
    // Reset to base fields when switching subtypes
    onChange({
      type: 'food_source',
      foodSourceType: newSubtype,
      boundaryGeoJSON: metadata.boundaryGeoJSON,
      plotSizeAcresCalculated: metadata.plotSizeAcresCalculated,
    } as Partial<FoodSourceMetadata>);
  }

  function handleBoundarySave(vertices: [number, number][]) {
    if (vertices.length < 3) return;
    const boundaryGeoJSON = verticesToGeoJSON(vertices);
    const plotSizeAcresCalculated = parseFloat(computeAcreage(vertices).toFixed(2));
    onChange({
      ...metadata,
      boundaryGeoJSON,
      plotSizeAcresCalculated,
    });
    setBoundaryModalOpen(false);
  }

  function handleBoundaryClear() {
    onChange({
      ...metadata,
      boundaryGeoJSON: undefined,
      plotSizeAcresCalculated: undefined,
    });
    setConfirmClear(false);
  }

  function renderSubtypeFields() {
    switch (foodSourceType) {
      case 'food_plot':
        return (
          <FoodPlotFields
            metadata={metadata as Partial<FoodPlotSubtype>}
            onChange={(updated) => onChange(updated as Partial<FoodSourceMetadata>)}
          />
        );
      case 'fruit_tree':
        return (
          <FruitTreeFields
            metadata={metadata as Partial<FruitTreeSubtype>}
            onChange={(updated) => onChange(updated as Partial<FoodSourceMetadata>)}
          />
        );
      case 'mineral_site':
        return (
          <MineralSiteFields
            metadata={metadata as Partial<MineralSiteSubtype>}
            onChange={(updated) => onChange(updated as Partial<FoodSourceMetadata>)}
          />
        );
      case 'feeder':
        return (
          <FeederFields
            metadata={metadata as Partial<FeederSubtype>}
            onChange={(updated) => onChange(updated as Partial<FoodSourceMetadata>)}
          />
        );
      case 'nuts':
        return (
          <NutsFields
            metadata={metadata as Partial<NutsSubtype>}
            onChange={(updated) => onChange(updated as Partial<FoodSourceMetadata>)}
          />
        );
      case 'berries':
        return (
          <BerriesFields
            metadata={metadata as Partial<BerriesSubtype>}
            onChange={(updated) => onChange(updated as Partial<FoodSourceMetadata>)}
          />
        );
      case 'other':
        return (
          <OtherFoodSourceFields
            metadata={metadata as Partial<OtherFoodSourceSubtype>}
            onChange={(updated) => onChange(updated as Partial<FoodSourceMetadata>)}
          />
        );
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Boundary drawing section — above type selector, only when center coords available */}
      {centerCoords && (
        <>
          <div className="flex flex-col gap-2">
            <p className="field-label">
              Boundary (optional)
            </p>

            {!hasBoundary ? (
              <button
                type="button"
                data-testid="draw-boundary-btn"
                onClick={() => setBoundaryModalOpen(true)}
                className="px-3 py-2 rounded-md text-body-sm text-[var(--color-text-secondary)] border border-[var(--color-border-subtle)] hover:border-[var(--color-border)] hover:text-[var(--color-text-primary)] transition-colors self-start"
              >
                Draw Boundary
              </button>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <span
                  className="text-body-sm text-[var(--color-text-secondary)] font-medium"
                  data-testid="acreage-badge"
                >
                  ~{calculatedAcreage} acres
                </span>
                <button
                  type="button"
                  data-testid="edit-boundary-btn"
                  onClick={() => setBoundaryModalOpen(true)}
                  className="px-3 py-1.5 rounded-md text-body-sm text-[var(--color-text-secondary)] border border-[var(--color-border-subtle)] hover:border-[var(--color-border)] hover:text-[var(--color-text-primary)] transition-colors"
                >
                  Edit Boundary
                </button>
                {confirmClear ? (
                  <>
                    <span className="text-body-xs text-[var(--color-text-placeholder)]">Remove boundary?</span>
                    <button
                      type="button"
                      onClick={handleBoundaryClear}
                      data-testid="confirm-clear-boundary-btn"
                      className="px-3 py-1.5 rounded-md text-body-sm text-red-400 border border-red-700 hover:border-red-500 transition-colors"
                    >
                      Yes, clear
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmClear(false)}
                      className="px-3 py-1.5 rounded-md text-body-sm text-[var(--color-text-placeholder)] border border-[var(--color-border)] hover:border-[var(--color-border-subtle)] transition-colors"
                    >
                      Keep
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    data-testid="clear-boundary-btn"
                    onClick={() => setConfirmClear(true)}
                    className="px-3 py-1.5 rounded-md text-body-sm text-[var(--color-text-placeholder)] border border-[var(--color-border)] hover:border-[var(--color-border-subtle)] hover:text-[var(--color-text-muted)] transition-colors"
                  >
                    Clear Boundary
                  </button>
                )}
              </div>
            )}
          </div>

          {/* key remounts the modal on each open, so useState re-initialises from initialVertices */}
          <BoundaryDrawingModal
            key={boundaryModalOpen ? 'open' : 'closed'}
            isOpen={boundaryModalOpen}
            onClose={() => setBoundaryModalOpen(false)}
            onSave={handleBoundarySave}
            center={centerCoords}
            initialVertices={currentVertices}
          />
        </>
      )}

      {/* Subtype selector */}
      <Select
        id="food-source-subtype"
        label="Food Source Type"
        value={foodSourceType}
        onChange={(v) => handleSubtypeChange(v as FoodSourceType)}
        options={SUBTYPE_OPTIONS}
      />

      {/* Type-specific fields */}
      {renderSubtypeFields()}
    </div>
  );
}
